import { createAsync, query, useNavigate } from "@solidjs/router";
import { Accessor, Show, Suspense } from "solid-js";
import {
  getHomeOnecardRouteData,
  HomeOnecardRouteData,
} from "./data/home_onecard_route_data";
import { FullScreenLoaderTelescope } from "~/components/webstore_telescope/full_screen_loader";
import { BrandGrid } from "./fragments/brand_grid";
import { HomeOnecardProvider } from "./context/home_onecard_context";
import { useHomeOnecard } from "./hooks/useHomeOnecard";
import { BackgroundGradientV2 } from "~/components/webstore/background_gradient_v2";
import { useWebStoreContext } from "~/context/webstore_context";
import { clientOnly } from "@solidjs/start";
import { HubbleBadge } from "~/components/webstore_telescope/hubble_badge";

const getHomeOnecardRouteData$C = query(
  getHomeOnecardRouteData,
  "home_onecard"
);

export default clientOnly(async () => ({ default: HomeOnecardView }), {
  lazy: true,
});

export function HomeOnecardView() {
  const routeData: Accessor<HomeOnecardRouteData | undefined> = createAsync(
    () => getHomeOnecardRouteData$C(),
    { deferStream: true }
  );

  return (
    <Suspense fallback={<FullScreenLoaderTelescope />}>
      <Show when={routeData()}>
        <HomeOnecardContent
          routeData={routeData as Accessor<HomeOnecardRouteData>}
        />
      </Show>
    </Suspense>
  );
}

function HomeOnecardContent(props: { routeData: Accessor<HomeOnecardRouteData> }) {
  const navigate = useNavigate();
  const onecardHook = useHomeOnecard(props.routeData);
  const { storeState, getPartnerConfigV3, checkIsOfferAvailed } =
    useWebStoreContext();

  const onBrandClick = (brandKey: string) => {
    navigate(`/buy-gift-card/${brandKey}`);
  };

  const contextValue = {
    routeData: props.routeData,
    onBrandClick,
    ...onecardHook,
  };

  return (
    <HomeOnecardProvider value={contextValue}>
      <div class="relative flex min-h-dvh w-full flex-col items-center bg-background-normal-primary">
        <Show when={getPartnerConfigV3().showGradient}>
          <BackgroundGradientV2
            color={getPartnerConfigV3().gradientColor ?? "#001EFF"}
          />
        </Show>

        {/* Hero Section */}
        <div class="z-[1] flex w-full max-w-[1024px] flex-col items-center px-3 sm:px-0">
          <div class="mt-8"></div>
          <h1 class="text-center text-title-6-regular text-text-normal-secondary">
            {getPartnerConfigV3().homepage?.smallTitle ??
              `Hi ${storeState.v4HomePage?.user?.firstName ?? ""} 👋`}
          </h1>
          <div class="mt-2"></div>
          <Show when={!checkIsOfferAvailed()}>
            <h2 class="max-w-[570px] text-center text-title-2-semi-bold text-text-normal-primary md:text-display-4-medium">
              {getPartnerConfigV3().homepage?.largeTitle ?? "Claim your reward"}
            </h2>
          </Show>
        </div>

        <div class="mt-8 sm:mt-10"></div>

        {/* Brand Grid or Already Used Message */}
        <Show
          when={checkIsOfferAvailed()}
          fallback={
            <Show
              when={
                props.routeData()?.brands &&
                props.routeData()!.brands.length > 0
              }
            >
              <div class="w-full pb-5">
                <BrandGrid
                  brands={props.routeData()!.brands}
                  onBrandClick={onBrandClick}
                  offerConstruct={storeState.offerConstruct}
                />
              </div>
            </Show>
          }
        >
          <div class="flex flex-col items-center px-4 text-center">
            <p class="text-body-1-regular text-text-normal-secondary">
              This code is already used. Please check your email for gift card
              details.
            </p>
          </div>
        </Show>

        {/* Footer */}
        <Show when={getPartnerConfigV3().showHubbleBranding}>
          <div class="mt-8"></div>
          <div class="fixed bottom-0 flex w-full flex-col gap-2 bg-background-normal-primary px-3 pb-2">
            <div class="h-px w-full bg-stroke-1" />
            <div class="flex w-full flex-col items-center">
              <HubbleBadge />
            </div>
          </div>
        </Show>
      </div>
    </HomeOnecardProvider>
  );
}
